import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { ThemeProvider } from "./context/ThemeContext";
import { ThemeMetaSync } from "./components/base/ThemeMetaSync";
import { BrowserRouter } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import routes from "./router/config";
import { withEntitlementGates } from "./router/entitlementGates";
import { withLimitesDeError } from "./router/limitesDeError";
import { AlertProvider } from "./context/AlertContext";
import { Suspense, useEffect } from "react";
import RouteFallback from "./router/RouteFallback";
import { onMessageListener, listenForSwNavigation, listenForSessionClosedSignal } from "./firebaseMessaging";
import { CustomNotification } from "./components/base/PushNotification";
import { AppToaster } from "./components/base/AppToaster";
import LimiteDeError from "./components/ui/LimiteDeError";
import { AnalyticsProvider } from "./components/analytics/AnalyticsProvider";
import { useUserStore } from "./store/userStore";
import { useAccessBootstrap } from "./hooks/useAccessBootstrap";
import { useClearOnUserChange } from "./lib/sessionIsolation";
import AvisosLoginModal from "./components/base/AvisosLoginModal";
import BillingGate from "./pages/purchase/BillingGate";
import SandboxBanner from "./features/sandbox/SandboxBanner";
import { listenForCrossTabLogout } from "./utils/remoteLogout";
import { TutorialHost } from "./tutorials";
import { InstallPrompt } from "./components/base/InstallPrompt";
import SecurityGate from "./security/SecurityGate";
import SecurityChallengeModal from "./security/SecurityChallengeModal";


// Cada ruta de primer nivel que el catálogo gobierna queda detrás de su guardia
// de banderas (épica #1219): una app en `off` (o `internal` para quien no opera
// banderas) redirige al Apps Hub. Se calcula una vez: el catálogo es estático.
// Y cada sección (lo que pinta el <Outlet /> de un layout) lleva su límite de
// error: un fallo se queda en la sección y el menú sigue para ir a otra.
const gatedRoutes = withLimitesDeError(withEntitlementGates(routes));

function AppRoutes() {
  return useRoutes(gatedRoutes);
}

function App() {
  const loadUserFromStorage = useUserStore((state) => state.loadUserFromStorage);

  useAccessBootstrap();
  // Al cambiar de usuario (cierre de sesión u otra cuenta) se vacía la caché de
  // consultas y los stores de la sesión: nada de la anterior se pinta en la nueva.
  useClearOnUserChange();
  
  useEffect(() => {
    // Cargar usuario persistido al iniciar la aplicación
    loadUserFromStorage();

    // Configurar listener para notificaciones push
    onMessageListener((props) => <CustomNotification {...props} />);

    // Configurar listeners para cierre remoto de sesiones
    listenForSwNavigation();
    listenForSessionClosedSignal();

    // Cierre de sesión en otra pestaña del navegador: BroadcastChannel("auth")
    // con el motivo y, de respaldo, el evento `storage` del token (mismo
    // mecanismo que CC-CONSOLE, ver utils/remoteLogout.ts). Un solo listener.
    return listenForCrossTabLogout();
  }, [loadUserFromStorage]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ThemeMetaSync />
        <BrowserRouter>
          <AnalyticsProvider>
            <AlertProvider>
              <AppToaster />
              {/* Empresa de autoalta sin licencia pagada → «Elige tu licencia» (#1009). */}
              <BillingGate />
              {/* 2FA obligatorio sin configurar → Perfil → Configuración. */}
              <SecurityGate />
              {/* Modo Sandbox encendido → leyenda arriba, con su botón de apagar. */}
              <SandboxBanner />
              {/*
                Sin esto, cualquier excepción en el render de CUALQUIER pantalla
                desmonta el árbol entero y la aplicación se queda en blanco: no
                un panel roto, todo. El operador pierde lo que estuviera
                haciendo y lo único que sabe reportar es «se puso en blanco».

                Va aquí, envolviendo las rutas, porque es la costura donde cabe
                todo sin tocar las 211 rutas una por una. Es la red de último
                recurso: lo que falla dentro de una sección lo atrapa antes el
                límite de esa sección (router/limitesDeError.tsx) y el
                encabezado y la barra lateral siguen en pie.
              */}
              <LimiteDeError zona="la aplicación" variante="pagina">
                {/* Las pantallas de router/config.tsx se cargan con React.lazy:
                    el primer render solo baja el chunk del login. */}
                <Suspense fallback={<RouteFallback />}>
                  <AppRoutes />
                </Suspense>
              </LimiteDeError>
              <AvisosLoginModal />
              {/* Diálogo del código: al entrar con 2FA y antes de una operación
                  de riesgo. Lo abre el interceptor de la API. */}
              <SecurityChallengeModal />
              {/* Tutoriales in-app (#1011): se ofrecen solos por pantalla y
                  el botón "?" (siempre visible con sesión) abre el de la pantalla, la
                  app Ayuda o Contactar a soporte. */}
              <TutorialHost />
              <InstallPrompt />
            </AlertProvider>
          </AnalyticsProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
